import { z } from "zod";

import { AIRLINE_CODE_PATTERN, AIRLINE_LABEL_MAX_NAMES, ANY_AIRLINES, MAX_AIRLINE_PICKS } from "./constants";
import type { AirlinePreference } from "./types";
import { effectiveAirlines } from "./workspace";

const airlineCode = z.string().trim().toUpperCase().regex(AIRLINE_CODE_PATTERN);

/** An airline choice as a search request or a saved snapshot may carry it: bounded codes and names. */
export const airlinePreferenceSchema = z.object({
  mode: z.enum(["any", "only", "exclude"]),
  codes: z.array(airlineCode).max(MAX_AIRLINE_PICKS),
  names: z.record(airlineCode, z.string().trim().min(1).max(120)).default({}),
});

/**
 * A stored airline choice read back, or any airline when it is missing or unreadable.
 * @param raw - The stored value.
 * @returns The choice.
 * @throws Never.
 */
export const parseAirlines = (raw: unknown): AirlinePreference => {
  const parsed = airlinePreferenceSchema.safeParse(raw);
  return parsed.success ? parsed.data : ANY_AIRLINES;
};

/**
 * What the trip bar adds after cabin and currency: "No Spirit", "Only United, Delta", "Only 3 airlines".
 * Short form names codes ("No NK") and counts past one ("Only 3").
 * @param airlines - The planner's choice.
 * @param short - Whether the bar is narrow.
 * @returns The label, or null for any airline.
 * @throws Never.
 */
export const airlinesLabel = (airlines: AirlinePreference, short = false): string | null => {
  const effective = effectiveAirlines(airlines);
  if (effective.mode === "any") return null;
  const prefix = effective.mode === "only" ? "Only" : "No";
  const { codes, names } = effective;
  if (short) return `${prefix} ${codes.length === 1 ? codes[0] : codes.length}`;
  if (codes.length > AIRLINE_LABEL_MAX_NAMES) return `${prefix} ${codes.length} airlines`;
  return `${prefix} ${codes.map((code) => shortAirlineName(names[code] ?? code)).join(", ")}`;
};

/** "Spirit Airlines" reads as "Spirit" in a label; "Air Canada" stays whole. */
const shortAirlineName = (name: string): string => name.replace(/\s+(Air ?lines|Airways)$/i, "") || name;
